import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatCard } from "@/components/kit";
import { usd } from "@/lib/format";
import { Wallet, TrendingUp, CalendarClock, ArrowDownRight, ArrowUpRight, Users, AlertTriangle } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";

export default function CashFlow() {
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  useEffect(() => {
    api.get("/cashflow/forecast").then((r) => setData(r.data));
    api.get("/cashflow/alerts").then((r) => setAlerts(r.data.alerts || []));
  }, []);
  if (!data) return <div className="text-navy font-display text-xl">Loading…</div>;

  const cf = data.snapshot;
  const chart = data.series.map((s) => ({ day: `D${s.day}`, cash: Math.round(s.cash_cents / 100), reserve: Math.round(data.reserve_cents / 100) }));

  return (
    <div>
      <PageHeader title="Cash Flow Manager" subtitle="Forecasts, not just reports — 90-day rolling projection updated nightly & on every event" />

      {alerts.map((a, i) => (
        <div key={i} data-testid="cf-alert" className={`rounded-xl p-4 flex items-start gap-3 mb-4 border-2 ${a.level === "danger" ? "bg-dangerr/5 border-dangerr/30" : "bg-amberw/5 border-amberw/30"}`}>
          <AlertTriangle className={`w-5 h-5 mt-0.5 ${a.level === "danger" ? "text-dangerr" : "text-amberw"}`} />
          <div><div className="font-bold text-navy">{a.title}</div><div className="text-sm text-slate-600">{a.message}</div>
            {a.drivers?.length ? <div className="text-xs text-slate-500 mt-1">Driven by: {a.drivers.join(" · ")}</div> : null}</div>
        </div>
      ))}

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        <StatCard testid="cf-available" label="Available Cash" value={usd(cf.available_cash_cents)} icon={Wallet} tone="navy" />
        <StatCard testid="cf-receivables" label="Receivables" value={usd(cf.receivables_cents)} icon={ArrowUpRight} tone="green" />
        <StatCard testid="cf-bills" label="Bills Due · 7 Days" value={usd(cf.bills_due_7d_cents)} icon={ArrowDownRight} tone="danger" />
        <StatCard testid="cf-deposits" label="Expected Deposits" value={usd(cf.expected_deposits_cents)} icon={CalendarClock} tone="navy" />
        <StatCard testid="cf-payroll" label="Payroll / 1099" value={usd(cf.payroll_labor_cents)} icon={Users} tone="amber" />
        <StatCard testid="cf-projected" label="Projected 30-Day" value={usd(cf.projected_30d_cents)} icon={TrendingUp} tone="green" />
      </div>

      <div className="mb-card p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold uppercase text-lg text-navy">90-Day Cash Projection</h3>
          {data.shortfall_day && <span className="text-xs font-semibold text-dangerr bg-dangerr/10 px-3 py-1 rounded-full">⚠ Projected shortfall around day {data.shortfall_day}</span>}
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={chart} margin={{ left: 10, right: 10 }}>
            <defs><linearGradient id="cash" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0B2545" stopOpacity={0.35} /><stop offset="100%" stopColor="#0B2545" stopOpacity={0} /></linearGradient></defs>
            <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#64748b" }} interval={2} />
            <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} width={50} />
            <Tooltip formatter={(v) => `$${Number(v).toLocaleString()}`} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontFamily: "JetBrains Mono" }} />
            <ReferenceLine y={Math.round(data.reserve_cents / 100)} stroke="#B42318" strokeDasharray="5 5" label={{ value: "Reserve", fontSize: 10, fill: "#B42318" }} />
            <Area type="monotone" dataKey="cash" stroke="#0B2545" strokeWidth={2.5} fill="url(#cash)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
